import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { zonasAPI, colmenasAPI, tareasAPI, alzasAPI, climaAPI } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { renderSug } from '../utils/sugerencias';

export default function Dashboard() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || 'es';
  const [zonas, setZonas] = useState([]);
  const [colmenas, setColmenas] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [alzasActivas, setAlzasActivas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clima, setClima] = useState(null);
  const [sugerencias, setSugerencias] = useState([]);
  const [newTaskFor, setNewTaskFor] = useState(null);

  useEffect(() => {
    const zonaPromise = zonasAPI.list();
    zonaPromise.then(z => {
      setZonas(z.data);
      const zonaConGPS = z.data.find(zona => zona.lat != null && zona.lng != null);
      if (zonaConGPS) {
        Promise.all([
          climaAPI.get(zonaConGPS.id),
          climaAPI.sugerencias(zonaConGPS.id),
        ])
          .then(([cl, sg]) => {
            if (cl.data && !cl.data.error) setClima(cl.data);
            const lista = sg.data?.sugerencias || [];
            if (lista.length > 0) setSugerencias(lista);
          })
          .catch(() => {});
      }
    });
    Promise.all([zonaPromise, colmenasAPI.list(), tareasAPI.list({ estado: 'pendiente' }), alzasAPI.activas()])
      .then(([_z, c, tsk, a]) => {
        setColmenas(c.data);
        setTareas(tsk.data);
        setAlzasActivas(a.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const colmenasActivas = colmenas.filter(c => c.activa);
  const activas = colmenasActivas.length;
  const pendientes = tareas.length;
  const hora = new Date().getHours();
  const saludoKey = hora < 12 ? 'dashboard.greeting_morning' : hora < 18 ? 'dashboard.greeting_afternoon' : 'dashboard.greeting_evening';

  const colmenasRojas = colmenasActivas.filter(c => c.semaforo === 'rojo');
  const colmenasAmarillas = colmenasActivas.filter(c => c.semaforo === 'amarillo');
  const colmenasVerdes = colmenasActivas.filter(c => c.semaforo === 'verde');

  const semaforoColor = { verde: '#4A7C59', amarillo: '#E8922A', rojo: '#C0392B' };
  const semaforoEmoji = { verde: '✅', amarillo: '⚠️', rojo: '🔴' };
  const prioridadColor = { alta: '#C0392B', normal: '#E8922A', baja: '#8B6355' };

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={S.title}>{t(saludoKey)}, {user?.name?.split(' ')[0]} 👋</h1>
        <p style={S.sub}>{t('dashboard.subtitle')}</p>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 60, color: '#8B6355' }}>{t('common.loading')}</div> : (
        <>
          <div style={S.statsGrid}>
            <StatCard icon="📍" label={t('dashboard.statZonas')} value={zonas.length} color="#E8922A" to="/zonas" />
            <StatCard icon="🐝" label={t('dashboard.statColmenas')} value={activas} color="#4A7C59" to="/colmenas" />
            <StatCard icon="📋" label={t('dashboard.statTareas')} value={pendientes} color="#8B6355" to="/tareas" />
            <StatCard icon="🚦" label={t('dashboard.statAlertas')} value={colmenasRojas.length} color="#C0392B" to="/colmenas" />
          </div>

          <Link to="/campo" style={{ textDecoration: 'none', display: 'block', marginBottom: 24 }}>
            <div style={{ background: '#E8922A', color: 'white', borderRadius: 14, padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, fontSize: 17, fontWeight: 700, fontFamily: "'DM Sans', sans-serif", boxShadow: '0 4px 16px rgba(232,146,42,0.35)', letterSpacing: '0.01em' }}>
              <span style={{ fontSize: 20 }}>⚡</span>
              {t('nav.campo')}
            </div>
          </Link>

          <FranjaClima clima={clima} sugerencias={sugerencias} t={t} lang={lang} />

          {colmenas.length > 0 && (
            <section style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ fontSize: 18, fontFamily: "'DM Sans', sans-serif" }}>{t('dashboard.semaforoTitle')}</h2>
                <Link to="/colmenas" className="btn btn-ghost btn-sm">{t('dashboard.semaforoLink')}</Link>
              </div>
              <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                <SemaforoBadge color="verde" count={colmenasVerdes.length} t={t} />
                <SemaforoBadge color="amarillo" count={colmenasAmarillas.length} t={t} />
                <SemaforoBadge color="rojo" count={colmenasRojas.length} t={t} />
              </div>
              <div style={S.semaforoGrid}>
                {colmenasActivas.map(c => {
                  const color = semaforoColor[c.semaforo] || '#4A7C59';
                  const noVerde = c.semaforo !== 'verde';
                  const razones = (c.alertas_activas || []).slice(0, 2).map(k => t(`estados.${k}`)).join(' · ');
                  return (
                    <div key={c.id} className="card card-body" style={{ padding: '10px 14px', borderLeft: `4px solid ${color}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 16 }}>{semaforoEmoji[c.semaforo] || '✅'}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nombre}</div>
                          <div style={{ fontSize: 11, color: '#8B6355' }}>{c.zona_nombre}</div>
                        </div>
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: color + '18', color, fontWeight: 600, flexShrink: 0 }}>
                          {c.semaforo === 'verde' ? t('dashboard.semOk') : c.semaforo === 'amarillo' ? t('dashboard.semWarning') : t('dashboard.semCritical')}
                        </span>
                      </div>
                      {noVerde && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, paddingTop: 6, borderTop: `1px solid ${color}20` }}>
                          {razones && <span style={{ fontSize: 11, color, fontStyle: 'italic', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{razones}</span>}
                          <button
                            onClick={() => setNewTaskFor(c)}
                            style={{ flexShrink: 0, marginLeft: 8, fontSize: 11, padding: '2px 8px', borderRadius: 6, border: `1px solid ${color}`, background: color + '14', color, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: 600, whiteSpace: 'nowrap' }}
                          >
                            + {t('tareas.newBtn').replace('+ ', '')}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {colmenasRojas.length > 0 && (
            <section style={{ marginBottom: 32 }}>
              <h2 style={{ fontSize: 18, fontFamily: "'DM Sans', sans-serif", marginBottom: 16 }}>{t('dashboard.alertasTitle')}</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {colmenasRojas.map(c => (
                  <div key={c.id} className="card card-body" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, background: '#FDF2F2', borderLeft: '4px solid #C0392B' }}>
                    <span style={{ fontSize: 20 }}>🐝</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#C0392B' }}>{c.nombre}</div>
                      <div style={{ fontSize: 12, color: '#8B6355' }}>{c.zona_nombre} &middot; {t('dashboard.alertasUrgent')}</div>
                    </div>
                    <Link to="/colmenas" className="btn btn-sm" style={{ background: '#C0392B', color: '#fff', fontSize: 12, padding: '4px 12px', borderRadius: 6 }}>{t('dashboard.alertasReview')}</Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          {alzasActivas.length > 0 && (
            <section style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ fontSize: 18, fontFamily: "'DM Sans', sans-serif" }}>{t('dashboard.alzasTitle')}</h2>
                <Link to="/bitacora" className="btn btn-ghost btn-sm">{t('dashboard.alzasLink')}</Link>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {alzasActivas.map(a => (
                  <div key={a.colmena_id} className="card card-body" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, background: '#F5F0FF', borderLeft: '4px solid #6B4FA0' }}>
                    <span style={{ fontSize: 20 }}>📦</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#6B4FA0' }}>{a.colmena_nombre}</div>
                      <div style={{ fontSize: 12, color: '#8B6355' }}>
                        {a.zona_nombre && <span>{a.zona_nombre} &middot; </span>}
                        {t('dashboard.alzaPlaced', { count: a.cantidad_alzas, s: a.cantidad_alzas !== 1 ? 's' : '', sp: a.cantidad_alzas !== 1 ? 's' : '', days: a.dias_activa, ds: a.dias_activa !== 1 ? 's' : '' })}
                      </div>
                    </div>
                    <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: '#6B4FA0', color: 'white', fontWeight: 600 }}>{t('dashboard.alzaActive')}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {tareas.length > 0 && (
            <section style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ fontSize: 18, fontFamily: "'DM Sans', sans-serif" }}>{t('dashboard.tareasTitle')}</h2>
                <Link to="/tareas" className="btn btn-ghost btn-sm">{t('dashboard.tareasLink')}</Link>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {tareas.slice(0, 5).map(tarea => (
                  <div key={tarea.id} className="card card-body" style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: prioridadColor[tarea.prioridad] || '#8B6355', flexShrink: 0 }}></span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tarea.descripcion}</div>
                      <div style={{ fontSize: 11, color: '#8B6355' }}>
                        {tarea.prioridad !== 'normal' && <span style={{ color: prioridadColor[tarea.prioridad], fontWeight: 600 }}>{tarea.prioridad === 'alta' ? t('tareas.priorityHigh') : t('tareas.priorityLow')} &middot; </span>}
                        {tarea.colmena_nombre && <span>{tarea.colmena_nombre} &middot; </span>}
                        {tarea.zona_nombre && <span>{tarea.zona_nombre}</span>}
                        {tarea.fecha_limite && <span> &middot; {new Date(tarea.fecha_limite).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  </div>
                ))}
                {tareas.length > 5 && (
                  <div style={{ textAlign: 'center', fontSize: 12, color: '#8B6355', padding: 8 }}>
                    {t('dashboard.tareasMore', { count: tareas.length - 5, s: tareas.length - 5 !== 1 ? 's' : '' })}
                  </div>
                )}
              </div>
            </section>
          )}

          {zonas.length > 0 ? (
            <section>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ fontSize: 18, fontFamily: "'DM Sans', sans-serif" }}>{t('dashboard.zonasTitle')}</h2>
                <Link to="/zonas" className="btn btn-ghost btn-sm">{t('dashboard.zonasLink')}</Link>
              </div>
              <div style={S.zonasGrid}>
                {zonas.slice(0, 4).map(z => (
                  <div key={z.id} className="card card-body" style={{ display: 'flex', gap: 12 }}>
                    <span style={{ fontSize: 20 }}>📍</span>
                    <div>
                      <div style={{ fontWeight: 500 }}>{z.nombre}</div>
                      <div style={{ fontSize: 12, color: '#E8922A' }}>{t('zonas.hiveCount', { count: z.colmenas_count, s: z.colmenas_count !== 1 ? 's' : '' })}</div>
                      {z.descripcion && <div style={{ fontSize: 12, color: '#8B6355', marginTop: 4 }}>{z.descripcion}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : (
            <div className="card card-body" style={{ textAlign: 'center', padding: 60, maxWidth: 400, margin: '0 auto' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🍯</div>
              <h2 style={{ fontFamily: "'DM Sans', sans-serif", marginBottom: 8 }}>{t('dashboard.welcomeTitle')}</h2>
              <p style={{ color: '#8B6355', marginBottom: 20 }}>{t('dashboard.welcomeMsg')}</p>
              <Link to="/zonas" className="btn btn-primary">{t('dashboard.welcomeBtn')}</Link>
            </div>
          )}
        </>
      )}

      {newTaskFor && (
        <MiniTaskModal
          colmena={newTaskFor}
          onClose={() => setNewTaskFor(null)}
          t={t}
        />
      )}
    </div>
  );
}

function MiniTaskModal({ colmena, onClose, t }) {
  const [desc, setDesc] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!desc.trim()) return;
    setSaving(true);
    try {
      await tareasAPI.create({ descripcion: desc, prioridad: 'normal', colmena_id: colmena.id });
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{t('tareas.modalTitle')}</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div style={{ marginBottom: 16, padding: '8px 12px', background: '#FFF8EE', borderRadius: 8, fontSize: 13, color: '#8B6355', border: '1px solid #FAE0B0' }}>
              🐝 {colmena.nombre}
              {colmena.zona_nombre && <span style={{ marginLeft: 6, color: '#C0A090' }}>· {colmena.zona_nombre}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">{t('tareas.descLabel')}</label>
              <input
                value={desc}
                onChange={e => setDesc(e.target.value)}
                className="form-input"
                placeholder={t('tareas.descPlaceholder')}
                autoFocus
                required
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !desc.trim()}>
              {saving ? t('common.saving') : t('tareas.createBtn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FranjaClima({ clima, sugerencias, t, lang }) {
  if (!sugerencias || sugerencias.length === 0) return null;

  const hoy = sugerencias[0];
  const semana = sugerencias[1] || null;
  const hoyMsg = renderSug(hoy, t, lang).msg;
  const semanaMsg = semana ? renderSug(semana, t, lang).msg : null;

  const bgHoy = hoy.tipo === 'inspeccion' && hoy.prioridad === 'info'
    ? '#F0FBF4'
    : hoy.prioridad === 'warning'
      ? '#FEF8EE'
      : '#F5F3F0';

  const borderHoy = hoy.tipo === 'inspeccion' && hoy.prioridad === 'info'
    ? '#4A7C59'
    : hoy.prioridad === 'warning'
      ? '#E8922A'
      : '#B0A898';

  const tempStr = clima && clima.temp != null
    ? `${Math.round(clima.temp)}°C · 💨 ${Math.round(clima.wind_kmh || 0)} km/h`
    : null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 32 }}>
      <div style={{ background: bgHoy, border: `1px solid ${borderHoy}30`, borderLeft: `4px solid ${borderHoy}`, borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#8B6355', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('dashboard.today')}</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>{hoy.icono}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#2C1A0E', lineHeight: 1.4 }}>{hoyMsg}</div>
            {tempStr && <div style={{ fontSize: 11, color: '#8B6355', marginTop: 4 }}>{tempStr}</div>}
          </div>
        </div>
        <div style={{ marginTop: 4 }}>
          <Link to="/clima" style={{ fontSize: 12, color: borderHoy, fontWeight: 600, textDecoration: 'none' }}>{t('common.seeMore')}</Link>
        </div>
      </div>

      {semana && (
        <div style={{ background: '#F8F6F3', border: '1px solid #E0D8D030', borderLeft: '4px solid #C8B89A', borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#8B6355', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('dashboard.thisWeek')}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 20, flexShrink: 0 }}>{semana.icono}</span>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#2C1A0E', lineHeight: 1.4 }}>{semanaMsg}</div>
          </div>
          <div style={{ marginTop: 4 }}>
            <Link to="/clima" style={{ fontSize: 12, color: '#8B6355', fontWeight: 600, textDecoration: 'none' }}>{t('common.seeMore')}</Link>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color, to, note }) {
  const content = (
    <div className="card" style={{ borderTop: `3px solid ${color}`, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
      <div style={{ width: 40, height: 40, borderRadius: 12, background: color + '18', color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{icon}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 24, fontWeight: 700, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.15 }}>{value}</div>
        <div style={{ fontSize: 12, color: '#8B6355', marginTop: 1, overflowWrap: 'break-word' }}>{label}</div>
        {note && <div style={{ fontSize: 10, color: '#C0A090', marginTop: 2, fontStyle: 'italic' }}>{note}</div>}
      </div>
    </div>
  );
  return to ? <Link to={to} style={{ textDecoration: 'none' }}>{content}</Link> : content;
}

function SemaforoBadge({ color, count, t }) {
  const colors = { verde: '#4A7C59', amarillo: '#E8922A', rojo: '#C0392B' };
  const labelKeys = { verde: 'dashboard.semOk', amarillo: 'dashboard.semWarning', rojo: 'dashboard.semCritical' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 20, background: colors[color] + '14', border: `1px solid ${colors[color]}30` }}>
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: colors[color] }}></span>
      <span style={{ fontSize: 13, fontWeight: 600, color: colors[color] }}>{count}</span>
      <span style={{ fontSize: 12, color: '#8B6355' }}>{t(labelKeys[color])}</span>
    </div>
  );
}

const S = {
  title: { fontSize: 28, fontFamily: "'DM Sans', sans-serif", color: '#2C1A0E' },
  sub: { color: '#8B6355', fontSize: 14, marginTop: 4 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 32 },
  zonasGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 },
  semaforoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 },
};
